import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Type, Loader2, Check, AlertTriangle, History, ChevronRight } from 'lucide-react';

/**
 * Database "Shorten Names" admin tool.
 *
 * Lets the operator submit a (long → short) rename, preview which
 * collections + fields would be touched, then apply the migration. The
 * ORIGINAL long-form value is preserved on each document as
 * `_original_<field>` so the full description remains available for
 * reference (search, tooltips, audit).
 *
 * Wired in /app/backend/routes/shorten_names.py.
 */
export default function ShortenNamesTool() {
  const { api } = useAuth();
  const [long, setLong] = useState('');
  const [short, setShort] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/shorten-names/history');
      setHistory(data?.entries || []);
    } catch {
      setHistory([]);
    }
  }, [api]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const runPreview = async () => {
    if (!long.trim() || !short.trim()) {
      toast.error('Fill both long and short forms');
      return;
    }
    setBusy(true);
    setPreview(null);
    try {
      const { data } = await api.post('/shorten-names/preview', { long, short });
      setPreview(data);
      if ((data?.total || 0) === 0) {
        toast.info('No documents contain that long-form string — nothing to do.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    }
    setBusy(false);
  };

  const apply = async () => {
    if (!preview || preview.total === 0) return;
    if (!window.confirm(
      `Rename "${preview.long}" → "${preview.short}" across ${preview.total} document(s)?\n` +
      `\nThe original value will be preserved on each document under _original_<field> for reference.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post('/shorten-names/apply', { long, short });
      toast.success(`Renamed ${data.affected} document(s).`);
      setPreview(null);
      setLong(''); setShort('');
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed');
    }
    setBusy(false);
  };

  return (
    <section data-testid="shorten-names-tool" className="bg-white border border-[#E2E2DF] rounded-md p-5 space-y-4">
      <header className="flex items-start gap-3">
        <div className="p-2 rounded-md bg-orange-50">
          <Type size={18} className="text-[#D35400]" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-slate-800">Shorten Names</h2>
          <p className="text-[11px] text-slate-500">Rename a long-form term to a short version across roof types, components, quote items, etc. The original full string stays available as <code className="bg-slate-100 px-1 rounded">_original_&lt;field&gt;</code> for reference.</p>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Long form (current)</label>
          <input
            data-testid="shorten-long-input"
            type="text" value={long} onChange={e => setLong(e.target.value)}
            placeholder="e.g. Galvanised Iron"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Short form (replace with)</label>
          <input
            data-testid="shorten-short-input"
            type="text" value={short} onChange={e => setShort(e.target.value)}
            placeholder="e.g. Galv Iron"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid="shorten-preview-btn" onClick={runPreview} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-40"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <ChevronRight size={11} />} Preview
        </button>
        <button
          data-testid="shorten-apply-btn" onClick={apply}
          disabled={busy || !preview || preview.total === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#D35400] text-white rounded-md hover:bg-[#A04000] disabled:opacity-40"
        >
          <Check size={11} /> Apply ({preview?.total || 0})
        </button>
      </div>

      {preview && preview.total > 0 && (
        <div data-testid="shorten-preview-output" className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs space-y-1">
          <p className="font-bold text-amber-900 flex items-center gap-1"><AlertTriangle size={11} /> Will rename <b>{preview.total}</b> document field(s):</p>
          <ul className="ml-5 list-disc text-slate-700">
            {Object.entries(preview.breakdown || {}).map(([coll, fields]) => (
              <li key={coll}>
                <b>{coll}</b>: {Object.entries(fields).map(([f, n]) => `${f} (${n})`).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {history.length > 0 && (
        <div className="pt-3 border-t border-slate-100">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1">
            <History size={10} /> Recent renames
          </p>
          <ul className="space-y-1 text-[11px]">
            {history.slice(0, 8).map(h => (
              <li key={h._id} className="flex items-center justify-between gap-2">
                <span className="truncate"><b className="text-slate-700">"{h.long}"</b> → <b className="text-emerald-700">"{h.short}"</b></span>
                <span className="text-slate-400 whitespace-nowrap">{h.affected} doc{h.affected === 1 ? '' : 's'} · {h.applied_by} · {new Date(h.applied_at).toLocaleString('en-NZ', { day: '2-digit', month: 'short' })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
